"use client";

/**
 * Agenda View
 * A phone-widget-style schedule: a rounded white card showing one day at a time — a big
 * day-number/month header, a day strip (only days that have items, auto-selecting today when
 * today is an event day), and colored-accent rows for that day. Untimed items are grouped under
 * "Anytime" and excluded from the live "now" marker.
 */

import type React from "react";
import { useEffect, useMemo, useState } from "react";

import { getButtonTextColor } from "@/features/landing-pages/_logic/color-utils";
import { formatDayNumber, formatDayOfWeek, formatDayOfWeekShort, formatMonthLong } from "@/lib/utils/formatting";
import type { ScheduleItemRow, ScheduleLabelRow } from "../../../../_logic/types/db-types";
import {
  buildTimedItems,
  computeLiveStatus,
  daysWithItems,
  selectDefaultDay,
} from "../../../../_logic/utils/schedule-utils";
import { useClientNow } from "../../../../_hooks/useNow";
import { useEventToday } from "../../../../_hooks/useEventToday";
import { SCHEDULE_UPCOMING_SOON_MINUTES } from "../../../../_logic/constants";
import { AgendaRow, type AgendaRowConfig } from "./AgendaRow";
import { DayChip } from "./DayChip";
import type { ViewerRegistrationKeys } from "../../../../_logic/utils/schedule-registration-match";

interface AgendaViewProps {
  dateGroups: { date: string | null; items: ScheduleItemRow[] }[];
  labels: ScheduleLabelRow[];
  /** Pre-computed label lookup map (computed once in ScheduleRenderer). */
  labelMap: Map<number, ScheduleLabelRow>;
  primaryColor: string;
  onDelete?: (item: ScheduleItemRow) => void;
  /** When provided, items become clickable (used in public view for the detail modal). */
  onItemClick?: (item: ScheduleItemRow) => void;
  /** Page-wide IANA timezone for rendering times. NULL = floating time (no live "now"). */
  timeZone: string | null;
  isPublic?: boolean;
  /** Viewer's confirmed registrations. `null` = no logged-in attendee. */
  viewerRegistrations: ViewerRegistrationKeys | null;
}

/** Renders schedule items as a single-day phone-widget agenda with a day strip. */
export function AgendaView({
  dateGroups,
  labels,
  labelMap,
  primaryColor,
  onDelete,
  onItemClick,
  timeZone,
  isPublic = false,
  viewerRegistrations,
}: AgendaViewProps): React.ReactElement {
  // Hooks
  const now = useClientNow();
  // Today (event zone, else viewer zone), resolved on mount to keep SSR markup stable.
  const todayStr = useEventToday(timeZone);

  // States
  const [selectedDate, setSelectedDate] = useState<string | null>(
    () => dateGroups.find((g) => g.date !== null)?.date ?? null
  );
  const [didInitToday, setDidInitToday] = useState(false);

  // Derived values
  const datedGroups = useMemo(
    () => dateGroups.filter((g): g is { date: string; items: ScheduleItemRow[] } => g.date !== null),
    [dateGroups]
  );
  const anytimeItems = useMemo(() => dateGroups.find((g) => g.date === null)?.items ?? [], [dateGroups]);
  const days = useMemo(() => daysWithItems(dateGroups), [dateGroups]);
  const activeChipTextColor = useMemo(() => getButtonTextColor(primaryColor), [primaryColor]);
  // Render context shared by every row (everything except the per-row item + live state). Grouped
  // into one stable object so AgendaRow takes a handful of props instead of a dozen.
  const rowConfig = useMemo<AgendaRowConfig>(
    () => ({ labels, labelMap, primaryColor, onDelete, onItemClick, timeZone, isPublic, viewerRegistrations }),
    [labels, labelMap, primaryColor, onDelete, onItemClick, timeZone, isPublic, viewerRegistrations]
  );

  const selectedItems = useMemo(
    () => datedGroups.find((g) => g.date === selectedDate)?.items ?? [],
    [datedGroups, selectedDate]
  );

  // Live "now" markers — restricted to the visible day, so only today's in-progress items light up.
  // Resolve instants once per day change; the per-tick memo only compares numbers.
  const timedSelected = useMemo(
    () => (timeZone ? buildTimedItems(selectedItems, timeZone) : []),
    [selectedItems, timeZone]
  );
  // Live "now" ids plus a map of imminent items → minutes-until-start, so the rows can show a "Now"
  // marker on in-progress items and a "Next · X min" badge on ones starting within the soon-window.
  const { liveItemIds, upcomingMinutesById } = useMemo(() => {
    const live = new Set<number>();
    const upcoming = new Map<number, number>();
    if (now == null) return { liveItemIds: live, upcomingMinutesById: upcoming };
    const status = computeLiveStatus(timedSelected, now, SCHEDULE_UPCOMING_SOON_MINUTES);
    if (status.kind === "live") {
      for (const entry of status.items) live.add(entry.item.id);
      for (const u of status.upcoming) upcoming.set(u.item.item.id, u.startsInMinutes);
    } else if (status.kind === "next" && status.startsInMinutes <= SCHEDULE_UPCOMING_SOON_MINUTES) {
      // Nothing live, but the next item is imminent — surface it too (the live state's `upcoming`
      // list is only populated while something is live).
      upcoming.set(status.item.item.id, status.startsInMinutes);
    }
    return { liveItemIds: live, upcomingMinutesById: upcoming };
  }, [timedSelected, now]);

  const isTodaySelected = selectedDate !== null && selectedDate === todayStr;

  // Effects — once today resolves (post-mount), prefer it when it is an event day. Runs once.
  useEffect(() => {
    if (didInitToday || todayStr == null) return;
    setSelectedDate(selectDefaultDay(days, todayStr));
    setDidInitToday(true);
  }, [didInitToday, todayStr, days]);

  // Effects — keep the selection valid when the day set changes (e.g. label filter)
  useEffect(() => {
    if (selectedDate !== null && !days.includes(selectedDate)) {
      setSelectedDate(days[0] ?? null);
    }
  }, [days, selectedDate]);

  // Render
  return (
    <div className="mx-auto w-full max-w-xl rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6">
      {/* Date header */}
      {selectedDate && (
        <div className="flex items-baseline gap-3">
          <span className="text-4xl font-bold leading-none tabular-nums text-gray-900">
            {formatDayNumber(selectedDate)}
          </span>
          <div className="flex flex-col">
            <span className="text-lg font-semibold text-gray-900">{formatMonthLong(selectedDate)}</span>
            <span className="text-sm font-medium text-gray-500">
              {isTodaySelected ? "Today · " : ""}
              {formatDayOfWeek(selectedDate)}
            </span>
          </div>
        </div>
      )}

      {/* Day strip */}
      {days.length > 1 && (
        <div
          role="group"
          aria-label="Select day"
          className="mt-4 flex items-center gap-1.5 overflow-x-auto"
          style={{ scrollbarWidth: "none" }}
        >
          {days.map((date) => (
            <DayChip
              key={date}
              weekdayShort={formatDayOfWeekShort(date)}
              dayNumber={formatDayNumber(date)}
              isActive={date === selectedDate}
              activeColor={primaryColor}
              activeTextColor={activeChipTextColor}
              ariaLabel={`${formatDayOfWeek(date)}, ${formatMonthLong(date)} ${formatDayNumber(date)}`}
              onClick={() => setSelectedDate(date)}
            />
          ))}
        </div>
      )}

      <div className="mt-4 border-t border-gray-100 pt-4">
        {selectedItems.length > 0 ? (
          <ul className="flex list-none flex-col gap-2">
            {selectedItems.map((item) => (
              <li key={item.id}>
                <AgendaRow
                  config={rowConfig}
                  item={item}
                  isLive={liveItemIds.has(item.id)}
                  startsInMinutes={upcomingMinutesById.get(item.id)}
                />
              </li>
            ))}
          </ul>
        ) : selectedDate !== null ? (
          <p className="py-6 text-center text-sm font-medium text-gray-500">Nothing scheduled for this day.</p>
        ) : null}

        {/* Anytime group — untimed/all-day items, excluded from the live computation */}
        {anytimeItems.length > 0 && (
          <div className="mt-4 flex flex-col gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Anytime</h3>
            <ul className="flex list-none flex-col gap-2">
              {anytimeItems.map((item) => (
                <li key={item.id}>
                  <AgendaRow config={rowConfig} item={item} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
